/**
 * ComprehensionEditor Component
 *
 * Editor for COMPREHENSION question types using payload.blanks structure.
 * Works directly with npm-pikado-logic's raw API format (NO customChoices abstraction).
 *
 * Features:
 * - Select which blank to edit by index
 * - Manage choices array (2-4 options per blank)
 * - Select correct answer from dropdown
 * - Add new choices (up to 4 per blank)
 */

import React from 'react';
import { Select, Input, Button, Typography  } from 'antd';
import type { QuestionWithNewFields } from '../../Questions/QuestionTypes';

const { Text } = Typography;

export interface ComprehensionEditorProps {
  question: QuestionWithNewFields;
  selectedBlankIndex: number;
  onSelectedBlankIndexChange: (index: number) => void;
  onChoiceValueChange: (blankIndex: number, choiceIndex: number, value: string) => void;
  onCorrectAnswerChange: (blankIndex: number, correctAnswer: string) => void;
  onAddChoice: (blankIndex: number) => void;
}

/**
 * Editor component for COMPREHENSION questions using payload.blanks
 */
export const ComprehensionEditor: React.FC<ComprehensionEditorProps> = ({
  question,
  selectedBlankIndex,
  onSelectedBlankIndexChange,
  onChoiceValueChange,
  onCorrectAnswerChange,
  onAddChoice,
}) => {
  // Get blanks from payload
  const blanks = question.payload?.blanks || [];

  // If no blanks exist, show a message
  if (blanks.length === 0) {
    return (
      <div style={{ marginTop: '20px', marginBottom: '20px' }}>
        <Text type="secondary">
          No CHOICES keywords detected. Please add CHOICES1, CHOICES2, etc. to your question text.
        </Text>
      </div>
    );
  }

  // Get the current blank
  const currentBlank = blanks[selectedBlankIndex];

  if (!currentBlank) {
    return (
      <div style={{ marginTop: '20px', marginBottom: '20px' }}>
        <Text type="warning">Selected blank not found.</Text>
      </div>
    );
  }

  return (
    <div>
      {/* Blank Selector */}
      <div style={{ marginTop: '20px', marginBottom: '20px' }}>
        <Text style={{ display: 'block' }}>Select Blank to Edit</Text>
        <Select
          value={selectedBlankIndex}
          showSearch
          style={{ width: '30%' }}
          placeholder="Select a blank"
          optionFilterProp="children"
          onChange={(selectedIndex: number) => onSelectedBlankIndexChange(selectedIndex)}
        >
          {blanks.map((blank, index) => (
            <Select.Option key={index} value={index}>
              {blank.name} ({blank.choices.length} choices)
            </Select.Option>
          ))}
        </Select>
      </div>

      {/* Blank Name Display */}
      <div style={{ marginTop: '10px', marginBottom: '10px' }}>
        <Text strong>Editing Blank: {currentBlank.name}</Text>
      </div>

      {/* Choice Options Editor */}
      <div>
        {/* Render input fields for each choice */}
        {currentBlank.choices.map((choiceValue, choiceIndex) => (
          <div key={choiceIndex} style={{ marginTop: '20px', marginBottom: '20px' }}>
            <Text>Choice {choiceIndex + 1}</Text>
            <Input
              value={choiceValue || ''}
              onChange={(e) =>
                onChoiceValueChange(selectedBlankIndex, choiceIndex, e.target.value)
              }
              placeholder={`Choice ${choiceIndex + 1}`}
            />
          </div>
        ))}

        {/* Add Choice Button (max 4 choices) */}
        <Button
          type="dashed"
          onClick={() => onAddChoice(selectedBlankIndex)}
          disabled={currentBlank.choices.length >= 4}
          style={{ marginTop: '10px', marginBottom: '20px' }}
        >
          Add Choice
        </Button>
        {currentBlank.choices.length >= 4 && (
          <Text type="secondary" style={{ marginLeft: '10px' }}>
            (Maximum 4 choices per blank)
          </Text>
        )}
      </div>

      {/* Correct Answer Selector */}
      <div style={{ marginTop: '20px', marginBottom: '20px' }}>
        <Text style={{ display: 'block' }}>
          Correct Answer <span style={{ color: 'red' }}>*</span>
        </Text>
        <Select
          value={currentBlank.correctAnswer || undefined}
          showSearch
          style={{ width: '100%', marginTop: '5px' }}
          placeholder="Select the correct answer"
          optionFilterProp="children"
          onChange={(selectedAnswer: string) =>
            onCorrectAnswerChange(selectedBlankIndex, selectedAnswer)
          }
        >
          {currentBlank.choices
            .filter((choice) => choice && choice.trim() !== '') // Only show non-empty choices
            .map((choice, index) => (
              <Select.Option key={index} value={choice}>
                {choice}
              </Select.Option>
            ))}
        </Select>
        {!currentBlank.correctAnswer && (
          <Text type="warning" style={{ display: 'block', marginTop: '5px' }}>
            Please select a correct answer
          </Text>
        )}
      </div>
    </div>
  );
};
